import { Component, OnInit, Input, ViewChild, ElementRef, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Observable, BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { StamdataStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/stamdata-status-enum';
import { ModelDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/model-dto';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
    selector: 'app-model-input',
    templateUrl: './model-input.component.html',
    styleUrls: ['./model-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModelInputComponent implements OnInit, OnDestroy {
  model: ModelDto;

  private _modeller = new BehaviorSubject<ModelDto[]>([]);
  @Input() set modeller(value: ModelDto[]) {
    this._modeller.next(value);
  }

  @Input() form: UntypedFormGroup;

  @Output() modelChange: EventEmitter<string> = new EventEmitter();

  get modeller() {
    return this._modeller.getValue();
  }

  get unknownModel() {
    return this.form.get('unknownModel');
  }

  filteredModeller: Observable<ModelDto[]>;

  _selectedModel$: ReplaySubject<string> = new ReplaySubject();

  @Input()
  set selectModel$(val: ReplaySubject<string>) {
    this._selectedModel$ = val;
  }

  selectedModel: string[] = [];

  @Input() appearance: string = 'outline';
  @Input() showLabel: boolean = true;
  @Input() formFieldName: string = 'modelId';
  @Input() formAlternativFieldName: string = 'alternativModel';

  modelCtrl = new UntypedFormControl();
  @ViewChild('modelInput') modelInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { read: MatAutocompleteTrigger }) matAutocomplete: MatAutocomplete;
  autocompleteTrigger: MatAutocompleteTrigger;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  visible = true;
  removable = true;

  modelLabel = 'Indtast model';
  unsubscribe$ = new Subject<void>();

  modelStatusEnum = StamdataStatusEnum;

  constructor() {}

  ngOnInit(): void {
    this._selectedModel$.pipe(takeUntil(this.unsubscribe$)).subscribe((model: string) => {
      if (model && model.trim() !== '') {
        this.selectedModel.push(model);
      } else {
        this.selectedModel = [];
      }
    });

    this._modeller.subscribe(() => {
      this.filteredModeller = this.modelCtrl.valueChanges.pipe(
        takeUntil(this.unsubscribe$),
        startWith(null),
        map((model: string | null) => (model ? this._filter(model) : []))
      );
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  addModel(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if (this.selectedModel.length === 0 && (value || '').trim()) {
      this.selectedModel.push(value.trim());
      this.modelChange.emit(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = null;
    }
  }

  removeModel(): void {
    this.model = null;
    this.selectedModel = [];
    this.modelCtrl.enable();
    this.modelChange.emit('');
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.model = this.modeller.find((x) => x.name === event.option.viewValue);
    this.selectedModel[0] = event.option.viewValue;
    this.modelInput.nativeElement.value = '';
    this.modelChange.emit(event.option.viewValue);
    this.modelCtrl.setValue(null);
  }

  unknownSlideChanged(event: MatSlideToggleChange) {
    this.unknownModel.setValue(event.checked);

    if (event.checked) {
      this.removeModel();
      this.modelChange.emit(null);
      this.modelLabel = 'Ukendt model';
    } else {
      this.modelChange.emit(null);
      this.modelLabel = 'Indtast model';
    }
  }

  private _filter(value: string): ModelDto[] {
    const filterValue = value.toLowerCase();
    return this.modeller
      .filter((model) => model.name.toLowerCase().indexOf(filterValue) === 0 && model !== this.selectedModel[0])
      .map((x) => x);
  }
}
